import {
  AdditionalHtsType,
  TariffAdditionalInfoFieldType,
} from '@xbcb/trade-data-types';

export interface ProductUsageQuestion {
  type: AdditionalHtsType;
  messageKey: string;
  fallback: string;
  order: number;
  fieldPath: string;
  fieldType: TariffAdditionalInfoFieldType;
  entryPath: string[];
  invert?: boolean;
}

export const PRODUCT_USAGE_QUESTIONS: ProductUsageQuestion[] = [
  {
    type: AdditionalHtsType.SECTION_232_AUTO_PARTS,
    messageKey: 'question_is_auto_part',
    fallback: 'Is this an automobile or auto part?',
    order: 10,
    fieldPath: 'productCharacteristics.isAutoOrAutoPart',
    fieldType: TariffAdditionalInfoFieldType.BOOLEAN,
    entryPath: ['productCharacteristics', 'isAutoPart'],
  },
  {
    type: AdditionalHtsType.SECTION_232_MHDV,
    messageKey: 'question_is_mhdv',
    fallback: 'Is this a medium or heavy-duty vehicle?',
    order: 20,
    fieldPath: 'productCharacteristics.isMediumHeavyDutyVehicle',
    fieldType: TariffAdditionalInfoFieldType.BOOLEAN,
    entryPath: ['productCharacteristics', 'isHeavyVehicleOrPart'],
  },
  {
    type: AdditionalHtsType.SECTION_232_WOOD_DERIVATIVE,
    messageKey: 'question_is_kitchen_cabinet',
    fallback: 'Is this a completed kitchen cabinet, vanity, or parts thereof?',
    order: 30,
    fieldPath: 'productCharacteristics.isKitchenCabinetVanityOrPart',
    fieldType: TariffAdditionalInfoFieldType.BOOLEAN,
    entryPath: ['productCharacteristics', 'isKitchenCabinetVanityOrPart'],
  },
];

export const getQuestionsForTypes = (
  types: (string | null | undefined)[],
): ProductUsageQuestion[] => {
  const typeSet = new Set(types.filter(Boolean));
  return PRODUCT_USAGE_QUESTIONS.filter((q) => typeSet.has(q.type)).sort(
    (a, b) => a.order - b.order,
  );
};

export const getQuestionsFromEntryState = (
  line: Record<string, unknown>,
): ProductUsageQuestion[] => {
  const characteristics = line?.productCharacteristics as
    | Record<string, unknown>
    | undefined;
  return PRODUCT_USAGE_QUESTIONS.filter((q) => {
    if (q.entryPath[0] === 'productCharacteristics') {
      if (!characteristics) return false;
      const key = q.entryPath[1];
      return (
        key &&
        characteristics[key] !== undefined &&
        characteristics[key] !== null
      );
    }
    const value = line?.[q.entryPath[0]];
    return value !== undefined && value !== null;
  }).sort((a, b) => a.order - b.order);
};

/**
 * Build the full product-usage question list for a line.
 *
 * Each re-evaluation returns only the candidates still awaiting an answer and
 * drops a candidate as soon as its question is answered. Rendering the
 * candidate list alone would therefore make answered questions disappear. So
 * we union the questions derived from answered entry state with those derived
 * from the current candidates, dedupe by type, and restore canonical order.
 */

export const getMergedQuestions = (
  line: Record<string, unknown>,
  candidateTypes: (string | null | undefined)[],
): ProductUsageQuestion[] => {
  const byType = new Map<AdditionalHtsType, ProductUsageQuestion>();
  for (const question of getQuestionsFromEntryState(line)) {
    byType.set(question.type, question);
  }
  for (const question of getQuestionsForTypes(candidateTypes)) {
    byType.set(question.type, question);
  }
  return [...byType.values()].sort((a, b) => a.order - b.order);
};
